import { supabase } from "@/lib/supabaseClient";

// ---------------------------------------------------------------------------
// "Mi WhatsApp" del socio: interruptor del agente, contactos autorizados e invitaciones.
// El agente solo responde a contactos que el socio agregó y dejó activos.
// ---------------------------------------------------------------------------

type Res = { ok?: boolean; error?: string };

async function call<T = Record<string, unknown>>(action: string, payload: Record<string, unknown> = {}): Promise<T & Res> {
  const { data, error } = await supabase.functions.invoke("wa-partner", { body: { action, ...payload } });
  if (error) {
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        const b = await ctx.json();
        return { ok: false, error: String(b?.error ?? "network") } as T & Res;
      }
    } catch { /* ignore */ }
    return { ok: false, error: "No se pudo conectar con el servidor." } as T & Res;
  }
  return data as T & Res;
}

export type PartnerWaState = {
  agentEnabled: boolean;
  displayName: string;
  mode: "shared" | "coexistence";
  whatsappReady: boolean;
  sharedNumber: string | null;
  globalAgentOn: boolean;
  inviteTemplateStatus: string;
};

export const fetchPartnerState = () => call<PartnerWaState>("state");
export const setPartnerAgent = (enabled: boolean) => call<{ agentEnabled: boolean }>("set_master", { enabled });

export type AddResult = { phone: string; name: string; status: "invited" | "added" | "invalid" | "taken" | "error"; detail?: string };

export const addPartnerContacts = (p: { contacts: { phone: string; name: string }[]; consent: boolean; invite: boolean; defaultCountry?: string }) =>
  call<{ results: AddResult[] }>("add_contacts", p);

export const invitePartnerContact = (contactId: string) => call("invite", { contact_id: contactId });
export const sendPartnerText = (conversationId: string, text: string) => call("send_text", { conversation_id: conversationId, text });

export type PartnerContact = {
  id: string;
  phone: string;
  name: string | null;
  agentEnabled: boolean;
  consent: boolean;
  invitedAt: string | null;
  prospectId: string | null;
  convStatus: "bot" | "human" | "closed" | null;
  unread: number;
  lastMessageAt: string | null;
  preview: string | null;
};

export async function fetchPartnerContacts(): Promise<PartnerContact[]> {
  const [{ data: cs, error }, { data: convs }] = await Promise.all([
    supabase.from("partner_wa_contacts").select("id, phone, name, agent_enabled, consent, invited_at, prospect_id").order("created_at", { ascending: false }).limit(500),
    supabase.from("wa_conversations").select("phone, status, unread, last_message_at, last_preview").limit(500),
  ]);
  if (error) throw error;
  const byPhone = new Map((convs ?? []).map((c) => [c.phone as string, c]));
  return (cs ?? []).map((c) => {
    const v = byPhone.get(c.phone as string);
    return {
      id: c.id,
      phone: c.phone,
      name: c.name,
      agentEnabled: c.agent_enabled,
      consent: c.consent,
      invitedAt: c.invited_at,
      prospectId: c.prospect_id,
      convStatus: (v?.status as PartnerContact["convStatus"]) ?? null,
      unread: v?.unread ?? 0,
      lastMessageAt: v?.last_message_at ?? null,
      preview: v?.last_preview ?? null,
    };
  });
}

export async function setContactAgent(id: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.from("partner_wa_contacts").update({ agent_enabled: enabled }).eq("id", id);
  if (error) throw error;
}

export async function removeContact(id: string): Promise<void> {
  const { error } = await supabase.from("partner_wa_contacts").delete().eq("id", id);
  if (error) throw error;
}
